import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { LocalStorageKeys, getTransformedDataForPagination, objectToQueryParams } from '../../../utils';

export const quoteAPI = createApi({
    reducerPath: "quoteAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "mm/quote",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),
    endpoints: (builder) => ({
        // Get Quotes API
        getQuotes: builder.query({
            query: ({ 
                page = 0, 
                pageSize = 10, 
                object_id, 
                object_type, 
                sort_by = null, 
                sort_order = null, 
                include_histories = false,
                tag_id = null, 
                tag_group_id = null, 
                search = ''
            }) => ({ url: objectToQueryParams({ 
                page_no: page + 1, 
                page_size: pageSize, 
                object_id, 
                object_type, 
                sort_by: sort_by, 
                sort_order: sort_order, 
                include_histories,
                tag_id,
                tag_group_id,
                search: search.trim() ? search : ""
            }), method: "GET" }),
            transformResponse: (data: any, meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),

        // Get Quote by ID API
        getQuotesById: builder.query({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
        }),

        // Create Quote API
        createQuote: builder.mutation({
            query: (payload: any) => ({ method: "POST", body: payload }),
        }),

        // Update Quote API
        updateQuote: builder.mutation({
            query: (payload: { id: string, payload: any, update_version?: boolean }) => ({ url: `/${payload.id}?update_version=${payload.update_version ? true : false}&generate_report=true`, method: "PUT", body: payload.payload }),
        }),

        // Delete Quote API
        deleteQuote: builder.mutation({
            query: ({ id }) => ({ url: `/${id}`, method: "DELETE" }),
        }),

        // Duplicate Quote API
        duplicateQuote: builder.mutation({
            query: (id: string) => ({ method: "POST", url: `/${id}` }),
        }),

        // Generate Quote Report
        generateQuoteReport: builder.mutation({
            query: (id: string) => ({ method: "POST", url: `${config.api_url}dm/quote_report/${id}/generate` }),
        }),
    })
})

// Export hooks for usage in functional components
export const {
    useGetQuotesQuery, useLazyGetQuotesQuery, useGetQuotesByIdQuery, useLazyGetQuotesByIdQuery,
    useCreateQuoteMutation, useUpdateQuoteMutation, useDeleteQuoteMutation, useDuplicateQuoteMutation,
    useGenerateQuoteReportMutation
} = quoteAPI